import { useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { api, ApiError } from "@/lib/api";
import { useSession } from "@/lib/session";

export function AcceptInvite() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const { token } = useParams<{ token: string }>();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/platform/auth/accept_invite", {
        token,
        name,
        password,
      });
      // Backend issues the session cookie on success — refresh and land home.
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : "Invite couldn't be accepted.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="w-full max-w-[420px] anim-fade-up">
      <div
        className="rounded-[var(--radius-modal)] p-8 shadow-xl-eon"
        style={{
          background: "rgba(11, 13, 18, 0.55)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(36px) saturate(180%)",
          WebkitBackdropFilter: "blur(36px) saturate(180%)",
        }}
      >
        <div className="mb-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-[var(--color-slate-200)]">
            Invitation
          </p>
          <h1 className="mt-1 text-heading text-[var(--color-snow)] font-semibold">
            Join your team
          </h1>
          <p className="mt-2 text-[14px] text-[var(--color-slate-200)]">
            Set up your account to start operating your agents.
          </p>
        </div>

        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <label className="block">
            <span className="block text-[12px] font-medium text-[var(--color-slate-200)] mb-1.5">
              Your name
            </span>
            <Input
              required
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <label className="block">
            <span className="block text-[12px] font-medium text-[var(--color-slate-200)] mb-1.5">
              Choose a password
            </span>
            <Input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          {error && (
            <p className="text-[13px] text-[var(--color-ember)]">{error}</p>
          )}

          <Button
            type="submit"
            disabled={submitting || !name || password.length < 8}
            className="w-full"
            size="lg"
          >
            {submitting ? "Setting up…" : "Accept and continue"}
          </Button>
        </form>
      </div>
    </div>
  );
}
